<template>
  <div>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" lg="9">
          <div
            class="d-flex flex-column flex-md-row align-center justify-center"
          >
            <v-autocomplete
              v-model="selectedServiceUserId"
              :items="selectUsers"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="service-user-select mb-3 mb-md-0 mr-md-3"
              :label="$t('select_user')"
            >
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="26" class="mr-2">
                    <img :src="item.avatarUrl" alt="avatar" />
                  </v-avatar>
                  <span>{{ item.text }}</span>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="28" class="mr-2">
                    <img :src="item.avatarUrl" alt="avatar" />
                  </v-avatar>
                  <span>{{ item.text }}</span>
                </div>
              </template>
            </v-autocomplete>
            <v-btn  color="primary" @click="openCreateDialog">
              <v-icon class="mr-2">mdi-account-plus</v-icon>{{ $t("create_user") }}
            </v-btn>
            <v-btn
              v-if="selectedServiceUserId"
              color="secondary"
              class="mt-2 mt-md-0 ml-md-2"
              @click="openCreateTokenDialog"
            >
              <v-icon class="mr-2">mdi-key-plus</v-icon>{{ $t("create_token") }}
            </v-btn>
            <v-btn
              v-if="selectedServiceUserId"
              color="error"
              class="mt-2 mt-md-0 ml-md-2"
              @click="deleteServiceUser"
            >
              <v-icon class="mr-2">mdi-account-remove</v-icon>{{ $t("delete") }}
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="selectedServiceUserId" justify="center">
        <v-col cols="12">
          <DataTables
            @deleteToken="deleteToken"
            @showAuthorizedRoutes="showAuthorizedRoutes"
            @editAuthorizedRoutes="editAuthorizedRoutes"
            @toggleTokenStatus="toggleTokenStatus"
            @copyToken="copyToken"
            :headers="tokenHeaders"
            :tableData="tokenTableData"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="createDialog" max-width="500" persistent>
      <v-card color="white" light style="margin: 0px;">
        <v-card-title class="headline">
          {{ $t("manage_service_account") }}
        </v-card-title>
        <v-card-text>
          <div class="d-flex justify-center mb-4">
            <my-img-uploader
              v-model="newServiceAccountAvatar"
              :width="200"
              :height="200"
              :placeholder="$t('click_drag_here')"
            ></my-img-uploader>
          </div>
          <v-text-field
            v-model="newServiceAccountName"
            :label="$t('name')"
            autofocus
            @keyup.enter="saveServiceAccount"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">{{ $t("back") }}</v-btn>
          <v-btn
            color="primary"
            :loading="isCreating"
            :disabled="!newServiceAccountName || !newServiceAccountName.trim()"
            @click="saveServiceAccount"
          >
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="manageRoutesDialog" max-width="900" persistent>
      <v-card color="white" light style="margin: 0px;">
        <v-card-title class="headline">
          {{ $t("authorized_routes") }}
        </v-card-title>
        <v-card-text>
          <v-row class="mb-2">
            <v-col cols="12" md="7">
              <v-text-field
                v-model="selectedTokenDescription"
                :label="$t('description')"
                dense
                outlined
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="5">
              <my-datepicker
                v-model="selectedTokenExpiryDate"
                :label="$t('expiry_date')"
                dense
                outlined
              ></my-datepicker>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                small
                color="primary"
                class="mr-2"
                @click="selectAllIntegrationEndpoints"
              >
                {{ $t("select_all") }}
              </v-btn>
              <v-btn
                small
                color="primary"
                @click="clearAllIntegrationEndpoints"
              >
                {{ $t("clear") }}
              </v-btn>
            </v-col>
          </v-row>

          <div
            style="max-height: 360px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 6px; padding: 8px 12px;"
          >
            <v-progress-linear
              v-if="isLoadingIntegrationEndpoints"
              indeterminate
              color="primary"
            ></v-progress-linear>

            <template v-else>
              <v-checkbox
                v-for="endpoint in integrationEndpoints"
                :key="endpoint"
                v-model="selectedIntegrationEndpoints"
                :label="endpoint"
                :value="endpoint"
                hide-details
                dense
                class="my-0"
              ></v-checkbox>

              <div v-if="integrationEndpoints.length === 0" class="text-body-2">
                {{ $t("no_data") }}
              </div>
            </template>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeManageRoutesDialog">{{ $t("back") }}</v-btn>
          <v-btn color="primary" :loading="isSavingRoutes" @click="saveAuthorizedRoutes">
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="createTokenDialog" max-width="520" persistent>
      <v-card color="white" light style="margin: 0px;">
        <v-card-title class="headline">
          {{ $t("create_token") }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newTokenDescription"
            :label="$t('description')"
            dense
            outlined
            class="mb-3"
          ></v-text-field>
          <my-datepicker
            v-model="newTokenExpiryDate"
            :label="$t('expiry_date')"
            dense
            outlined
          ></my-datepicker>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateTokenDialog">{{ $t("back") }}</v-btn>
          <v-btn
            color="primary"
            :loading="isCreatingToken"
            @click="createToken"
          >
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataTables from "@/components/base/DataTables.vue";
import MyDatepicker from "@/components/base/MyDatepicker.vue";
import MyImageUploader from "@/components/base/MyImageUploader.vue";
import Swal from "sweetalert2";

export default {
  name: "manage-service-account",
  components: {
    DataTables,
    MyDatepicker,
    "my-img-uploader": MyImageUploader,
  },
  data() {
    return {
      isFetching: false,
      selectUsers: [],
      selectedServiceUserId: null,
      tokenHeaders: [],
      tokenTableData: [],
      createDialog: false,
      createTokenDialog: false,
      manageRoutesDialog: false,
      newServiceAccountName: "",
      newServiceAccountAvatar: null,
      isCreating: false,
      isCreatingToken: false,
      newTokenDescription: "",
      newTokenExpiryDate: null,
      integrationEndpoints: [],
      selectedIntegrationEndpoints: [],
      isLoadingIntegrationEndpoints: false,
      isSavingRoutes: false,
      selectedTokenForRoutes: null,
      selectedTokenDescription: "",
      selectedTokenExpiryDate: null,
    };
  },
  methods: {
    escapeHtml(text) {
      if (text === null || text === undefined) return "";
      return text
        .toString()
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    toRouteKey(route) {
      if (!route) return "";
      if (typeof route === "string") {
        return this.normalizeRouteKey(route);
      }

      const method = route.method ? route.method.toString().toUpperCase().trim() : "";
      let path = route.route ? route.route.toString().trim() : "";
      if (!method || !path) return "";
      if (!path.startsWith("/")) path = "/" + path;
      return `[${method}]${path}`;
    },
    normalizeRouteKey(routeLike) {
      if (!routeLike) return "";

      if (typeof routeLike === "object") {
        return this.toRouteKey(routeLike);
      }

      const value = routeLike.toString().trim();
      if (!value) return "";

      const bracketMatch = value.match(/^\[([A-Za-z]+)\](.+)$/);
      if (bracketMatch) {
        const method = bracketMatch[1].toUpperCase().trim();
        let path = bracketMatch[2].trim();
        if (!path.startsWith("/")) path = "/" + path;
        return `[${method}]${path}`;
      }

      const spacedMatch = value.match(/^([A-Za-z]+)\s+(.+)$/);
      if (spacedMatch) {
        const method = spacedMatch[1].toUpperCase().trim();
        let path = spacedMatch[2].trim();
        if (!path.startsWith("/")) path = "/" + path;
        return `[${method}]${path}`;
      }

      if (value.startsWith("/")) return `[GET]${value}`;
      return value;
    },
    formatTokenTwoLines(token) {
      if (!token) return "";
      return token.toString();
    },
    parseAuthorizedRoutesInput(inputText) {
      if (!inputText || !inputText.trim()) return [];

      return inputText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line)
        .map((line) => {
          const bracketMatch = line.match(/^\[([A-Za-z]+)\](.+)$/);
          if (bracketMatch) {
            return {
              method: bracketMatch[1].toUpperCase(),
              route: bracketMatch[2].trim(),
            };
          }

          const spacedMatch = line.match(/^([A-Za-z]+)\s+(.+)$/);
          if (spacedMatch) {
            return {
              method: spacedMatch[1].toUpperCase(),
              route: spacedMatch[2].trim(),
            };
          }

          return {
            method: "GET",
            route: line,
          };
        });
    },
    openCreateDialog() {
      this.newServiceAccountName = "";
      this.newServiceAccountAvatar = null;
      this.createDialog = true;
    },
    closeCreateDialog() {
      this.createDialog = false;
      this.newServiceAccountName = "";
      this.newServiceAccountAvatar = null;
    },
    closeManageRoutesDialog() {
      this.manageRoutesDialog = false;
      this.selectedIntegrationEndpoints = [];
      this.selectedTokenForRoutes = null;
      this.selectedTokenDescription = "";
      this.selectedTokenExpiryDate = null;
      this.integrationEndpoints = [];
    },
    selectAllIntegrationEndpoints() {
      this.selectedIntegrationEndpoints = [...this.integrationEndpoints];
    },
    clearAllIntegrationEndpoints() {
      this.selectedIntegrationEndpoints = [];
    },
    routeKeyToObject(routeKey) {
      if (!routeKey || typeof routeKey !== "string") return null;
      const normalizedRouteKey = this.normalizeRouteKey(routeKey);
      const match = normalizedRouteKey.match(/^\[([A-Za-z]+)\](.+)$/);
      if (!match) return null;
      return {
        method: match[1].toUpperCase(),
        route: match[2].trim(),
      };
    },
    normalizeIntegrationEndpointsResponse(response) {
      if (Array.isArray(response)) return response;
      if (!response) return [];

      if (Array.isArray(response.data)) return response.data;
      if (response.data && Array.isArray(response.data.data)) return response.data.data;

      return [];
    },
    normalizeExpiryDatePayload(value) {
      const normalized = value ? value.toString().trim() : "";
      return normalized !== "" ? normalized : null;
    },
    getDefaultTokenExpiryDate() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      date.setFullYear(date.getFullYear() + 3);
      return date.toISOString();
    },
    openCreateTokenDialog() {
      if (!this.selectedServiceUserId) return;
      this.newTokenDescription = "";
      this.newTokenExpiryDate = this.getDefaultTokenExpiryDate();
      this.createTokenDialog = true;
    },
    closeCreateTokenDialog() {
      this.createTokenDialog = false;
      this.newTokenDescription = "";
      this.newTokenExpiryDate = null;
    },
    createToken() {
      if (!this.selectedServiceUserId || this.isCreatingToken) return;

      this.isCreatingToken = true;
      this.chiamataAxios(
        "PUT",
        "/auth/portal/createServiceUserToken/" + this.selectedServiceUserId,
        {
          body: {
            description: (this.newTokenDescription || "").trim(),
            expiryDate: this.normalizeExpiryDatePayload(this.newTokenExpiryDate),
          },
          msg: "always",
        }
      )
        .then((response) => {
          if (response && response.msg && response.msg.esito) {
            this.closeCreateTokenDialog();
            this.loadServiceUsers();
          }
        })
        .finally(() => {
          this.isCreatingToken = false;
        });
    },
    async copyToken(item) {
      if (!item || !item.token) return;

      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(item.token);
        } else {
          const temp = document.createElement("textarea");
          temp.value = item.token;
          document.body.appendChild(temp);
          temp.select();
          document.execCommand("copy");
          document.body.removeChild(temp);
        }

        Swal.fire({
          type: "success",
          text: this.$t("operation_completed"),
          customClass: "v-application",
        });
      } catch (e) {
        Swal.fire({
          type: "error",
          text: this.$t("error"),
          customClass: "v-application",
        });
      }
    },
    deleteToken(item) {
      if (!this.selectedServiceUserId || !item || !item.token) return;

      Swal.fire({
        title: this.$t("are_you_sure_?"),
        text: this.$t("you_cant_revert_this"),
        type: "warning",
        showCancelButton: true,
        customClass: "v-application",
        confirmButtonClass: "md-button md-success",
        cancelButtonClass: "md-button md-danger",
        confirmButtonText: this.$t("yes_delete"),
        cancelButtonText: this.$t("back"),
        buttonsStyling: true,
      }).then((result) => {
        if (!result.value) return;

        this.chiamataAxios(
          "DELETE",
          "/auth/portal/deleteServiceUserToken/" +
            this.selectedServiceUserId +
            "/" +
            encodeURIComponent(item.token),
          { msg: "always" }
        ).then((response) => {
          if (response && response.msg && response.msg.esito) {
            this.loadServiceUsers();
          }
        });
      });
    },
    deleteServiceUser() {
      if (!this.selectedServiceUserId) return;

      Swal.fire({
        title: this.$t("are_you_sure_?"),
        text: this.$t("you_cant_revert_this"),
        type: "warning",
        showCancelButton: true,
        customClass: "v-application",
        confirmButtonClass: "md-button md-success",
        cancelButtonClass: "md-button md-danger",
        confirmButtonText: this.$t("yes_delete"),
        cancelButtonText: this.$t("back"),
        buttonsStyling: true,
      }).then((result) => {
        if (!result.value) return;

        this.chiamataAxios(
          "DELETE",
          "/auth/portal/deleteServiceUser/" + this.selectedServiceUserId,
          { msg: "always" }
        ).then((response) => {
          if (response && response.msg && response.msg.esito) {
            this.selectedServiceUserId = null;
            this.tokenTableData = [];
            this.loadServiceUsers();
          }
        });
      });
    },
    showAuthorizedRoutes(item) {
      const routes = item && item.authorizedRoutesText ? item.authorizedRoutesText : "-";
      Swal.fire({
        title: this.$t("authorized_routes"),
        text: routes,
        type: "info",
        customClass: "v-application",
      });
    },
    editAuthorizedRoutes(item) {
      if (!this.selectedServiceUserId || !item || !item.token) return;

      this.selectedTokenForRoutes = item;
      this.selectedIntegrationEndpoints = [...(item.authorizedRoutesKeys || [])];
      this.selectedTokenDescription = item.description || "";
      this.selectedTokenExpiryDate = item.expiryDate || null;
      this.isLoadingIntegrationEndpoints = true;

      this.chiamataAxios("GET", "/auth/portal/listIntegrationEndpoints", {
        msg: "none",
      })
        .then((endpointsResponse) => {
          this.integrationEndpoints = this.normalizeIntegrationEndpointsResponse(
            endpointsResponse
          )
            .map((endpoint) => this.normalizeRouteKey(endpoint))
            .filter((endpoint) => endpoint !== "");

          const selectedByKey = new Set(
            (item.authorizedRoutesKeys || [])
              .map((route) => this.normalizeRouteKey(route))
              .filter((route) => route)
          );

          this.selectedIntegrationEndpoints = this.integrationEndpoints.filter((endpoint) =>
            selectedByKey.has(endpoint)
          );
          this.manageRoutesDialog = true;
        })
        .finally(() => {
          this.isLoadingIntegrationEndpoints = false;
        });
    },
    saveAuthorizedRoutes() {
      if (
        !this.selectedServiceUserId ||
        !this.selectedTokenForRoutes ||
        !this.selectedTokenForRoutes.token
      ) {
        return;
      }

      const routes = (this.selectedIntegrationEndpoints || [])
        .map((key) => this.routeKeyToObject(key))
        .filter((route) => route);

      this.isSavingRoutes = true;
      this.chiamataAxios(
        "POST",
        "/auth/portal/updateServiceUserToken/" +
          this.selectedServiceUserId +
          "/" +
          encodeURIComponent(this.selectedTokenForRoutes.token),
        {
          body: {
            description: (this.selectedTokenDescription || "").trim(),
            expiryDate: this.normalizeExpiryDatePayload(this.selectedTokenExpiryDate),
            authorizedRoutes: routes,
          },
          msg: "always",
        }
      )
        .then((response) => {
          if (response && response.msg && response.msg.esito) {
            this.closeManageRoutesDialog();
            this.loadServiceUsers();
          }
        })
        .finally(() => {
          this.isSavingRoutes = false;
        });
    },
    toggleTokenStatus(item) {
      if (!this.selectedServiceUserId || !item || !item.token) return;

      const isEnabled =
        item.enabled && item.enabled[0] && item.enabled[0].icon === "mdi-check-outline";

      this.chiamataAxios(
        "POST",
        "/auth/portal/updateServiceUserToken/" +
          this.selectedServiceUserId +
          "/" +
          encodeURIComponent(item.token),
        {
          body: {
            enabled: !isEnabled,
          },
          msg: "always",
        }
      ).then((response) => {
        if (response && response.msg && response.msg.esito) {
          this.loadServiceUsers();
        }
      });
    },
    buildTokenTable() {
      const selectedUser = this.selectUsers.find(
        (item) => item.value === this.selectedServiceUserId
      );

      if (!selectedUser || !Array.isArray(selectedUser.tokens)) {
        this.tokenTableData = [];
        return;
      }

      this.tokenTableData = selectedUser.tokens.map((tokenItem, index) => {
        const routes = Array.isArray(tokenItem.authorizedRoutes)
          ? tokenItem.authorizedRoutes
              .map((route) => `${route.method || ""} ${route.route || ""}`.trim())
              .filter((route) => route)
              .join("<br>")
          : "";
        const routesKeys = Array.isArray(tokenItem.authorizedRoutes)
          ? tokenItem.authorizedRoutes
              .map((route) => this.normalizeRouteKey(route))
              .filter((route) => route)
          : [];
        const routesText = Array.isArray(tokenItem.authorizedRoutes)
          ? tokenItem.authorizedRoutes
              .map((route) => `${route.method || ""} ${route.route || ""}`.trim())
              .filter((route) => route)
              .join("\n")
          : "";

        return {
          idx: index + 1,
          copyOptions: [
            {
              icon: "mdi-content-copy",
              color: "primary",
              text: "copia token",
              function: "copyToken",
            },
          ],
          token: tokenItem.token || "",
          tokenDisplay: this.formatTokenTwoLines(tokenItem.token || ""),
          description: tokenItem.description || "",
          expiryDate: tokenItem.expiryDate || null,
          totalEnabledRoutes: routesKeys.length,
          authorizedRoutesKeys: routesKeys,
          authorizedRoutesText: routesText,
          lastAgent: tokenItem.lastAgent || "",
          lastLogin: tokenItem.lastLogin || null,
          enabled: tokenItem.enabled
            ? [{ icon: "mdi-check-outline", color: "green" }]
            : [{ icon: "mdi-close-outline", color: "red" }],
          options: [
            {
              icon: "mdi-delete-outline",
              color: "error",
              text: "elimina token",
              function: "deleteToken",
            },
            {
              icon: "mdi-pencil-outline",
              color: "secondary",
              text: "modifica rotte autorizzate",
              function: "editAuthorizedRoutes",
            },
            {
              icon: "mdi-toggle-switch-outline",
              color: "warning",
              text: "attiva/disattiva",
              function: "toggleTokenStatus",
            },
          ],
        };
      });
    },
    saveServiceAccount() {
      const name = (this.newServiceAccountName || "").trim();
      if (!name || this.isCreating) return;

      this.isCreating = true;

      this.chiamataAxios("PUT", "/auth/portal/createServiceUser", {
        body: {
          name: name,
          avatar: this.newServiceAccountAvatar,
        },
        msg: "always",
      }).then((response) => {
          if (response && response.msg && response.msg.esito) {
            this.closeCreateDialog();
            this.loadServiceUsers();
          }
        })
        .finally(() => {
          this.isCreating = false;
        });
    },
    loadServiceUsers() {
      this.isFetching = true;

      this.chiamataAxios("GET", "/auth/portal/listServiceUsers")
        .then((response) => {
          const prevSelectedId = this.selectedServiceUserId;

          this.selectUsers = (response.data || []).map((item) => {
            return {
              value: item.masterId,
              text: item.name,
              avatarUrl: item.avatarUrl || "/img/avatar/default.png",
              tokens: item.tokens || [],
            };
          });

          if (
            this.selectedServiceUserId &&
            !this.selectUsers.some((item) => item.value === this.selectedServiceUserId)
          ) {
            this.selectedServiceUserId = null;
          }

          if (this.selectedServiceUserId || prevSelectedId) {
            this.buildTokenTable();
          }
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
  },
  watch: {
    selectedServiceUserId() {
      this.buildTokenTable();
    },
  },
  mounted() {
    this.tokenHeaders = [
      { text: "", value: "copyOptions", type: "buttons" },
      { text: this.$t("id"), value: "idx" },
      { text: this.$t("token"), value: "tokenDisplay" },
      { text: this.$t("description"), value: "description" },
      { text: this.$t("expiry_date"), value: "expiryDate", type: "datetime" },
      { text: this.$t("total_enabled_routes"), value: "totalEnabledRoutes" },
      { text: this.$t("last_agent"), value: "lastAgent" },
      { text: this.$t("last_login"), value: "lastLogin", type: "datetime" },
      { text: this.$t("enabled"), value: "enabled", type: "icons" },
      { text: this.$t("options"), value: "options", type: "buttons" },
    ];
    this.loadServiceUsers();
  },
};
</script>

<style scoped>
.service-user-select {
  width: 100%;
  max-width: 560px;
}
</style>
